import { getApiBaseUrl } from '../../utils/environment.mjs';
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authProvider.mjs';
import PageShell from '../../components/PageShell.mjs';

const API_URL = getApiBaseUrl();

// Indonesia and South Asia were withdrawn; see LEGACY_REGIONS in
// auditor/page/PTAERegions.mjs, which still labels them on historical records.
const REGIONS = [
  { value: 'West-Africa', label: 'West Africa' },
  { value: 'East-Africa', label: 'East Africa' },
  { value: 'North-America', label: 'North America' },
  { value: 'India', label: 'India' }
];

const CompleteProfile = () => {
  const [region, setRegion] = useState('');
  const [position, setPosition] = useState('');
  // The position the account already has - filled in at sign-in from the
  // Stanford directory title when Stanford releases it (2026-10-22). Shown for
  // them to confirm or change; empty means they type it, as before.
  const [prefilledPosition, setPrefilledPosition] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleProfile, setGoogleProfile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshAuthState, isAuthenticated, isLoading } = useContext(AuthContext);

  useEffect(() => {
    const stored = sessionStorage.getItem('pendingGoogleProfile');
    if (stored) {
      try {
        setGoogleProfile(JSON.parse(stored));
      } catch {
        // Corrupted or tampered sessionStorage value - discard silently
      }
      sessionStorage.removeItem('pendingGoogleProfile');
    }
  }, []);

  // Start from what the account already holds, once it has loaded.
  useEffect(() => {
    if (user?.position && !position) {
      setPosition(user.position);
      setPrefilledPosition(user.position);
    }
    if (user?.region && !region) setRegion(user.region);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.position, user?.region]);

  // Redirect if user already has a complete profile or is not authenticated
  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        navigate('/login');
      } else if (user?.region && user?.position) {
        // Profile already complete - go where they were headed, else dashboard.
        navigate(location.state?.returnUrl || '/dashboard');
      }
    }
  }, [isAuthenticated, isLoading, user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!region || !position.trim()) {
      setError('Please select a region and enter your position');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/complete-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ region, position })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh auth state to get updated user data
        await refreshAuthState();
        // Return the user to whatever they were trying to open, if anything.
        navigate(location.state?.returnUrl || '/dashboard');
      } else {
        setError(data.message || 'Failed to complete profile');
      }
    } catch (err) {
      setError('Failed to complete profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PageShell variant="entry">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 w-full max-w-md flex justify-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin"></div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell variant="entry">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-5 sm:p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Complete Your Profile</h2>
          <p className="text-gray-600">
            Welcome{user?.firstName ? `, ${user.firstName}` : ''}! Please complete your profile to continue.
          </p>
        </div>

        {(user?.profilePicture || (!isLoading && googleProfile?.picture)) && (() => {
          // Sanitize profile picture URL - only allow https: to prevent script injection via src
          const rawPic = user?.profilePicture || googleProfile?.picture;
          let safePic = null;
          try { safePic = new URL(rawPic).protocol === 'https:' ? rawPic : null; } catch { safePic = null; }
          return safePic ? (
            <div className="flex justify-center mb-6">
              <img
                src={safePic}
                alt="Profile"
                className="w-20 h-20 rounded-full border-4 border-stanford-red"
              />
            </div>
          ) : null;
        })()}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={user?.firstName || (!isLoading ? googleProfile?.firstName : '') || ''}
                readOnly
                className="w-full px-3 sm:px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={user?.lastName || (!isLoading ? googleProfile?.lastName : '') || ''}
                readOnly
                className="w-full px-3 sm:px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={user?.email || (!isLoading ? googleProfile?.email : '') || ''}
              readOnly
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
              Region <span className="text-red-500">*</span>
            </label>
            <select
              id="region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent hover:border-gray-400 transition-colors"
            >
              <option value="">Select a region</option>
              {REGIONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
              Position <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="e.g., Research Associate"
              required
              aria-describedby={prefilledPosition ? 'position-source' : undefined}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent hover:border-gray-400 transition-colors"
            />
            {prefilledPosition && position === prefilledPosition && (
              <p id="position-source" className="text-xs text-gray-500 mt-1">
                Filled in from your Stanford profile. Change it if it is not right.
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !region || !position.trim()}
            className="w-full bg-stanford-red text-white py-3 px-4 rounded-lg font-medium hover:bg-stanford-light-red transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-stanford-red focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {loading ? 'Saving...' : 'Complete Profile'}
          </button>
        </form>
      </div>
    </PageShell>
  );
};

export default CompleteProfile;
